import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { RecipientsCount } from '../../../../admin/components/email/RecipientsCount';
import { DefaultBulkContactsRequest } from '../../../../extViewmodels/Utils';
import { ContactSortKey, IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { shouldDisableOwnershipDropdown } from '../../../../models/UiUtils';
import { useCustomFieldLabelMap } from '../../../../queries/Contact/useContactCustomFields';
import { isPolicyBoardEnabled } from '../../../../models/accountFeatures';
import { useContactRenewalPolicies } from '../../../../queries/InsurancePolicy/useContactRenewalPolicies';
import {
	ComposeEmailViewModel,
	ComposeFollowUpEmailViewModel,
	ComposeResourceEmailViewModel,
	ContactFilterCriteriaProperty,
	ContactViewModel,
	DefaultBulkSendExcludedTags,
	IContactFilterCriteria,
	IContactsFilterRequest,
	IOperationResultNoValue,
	VmUtils,
} from '../../../../viewmodels/AppViewModels';
import { AdvancedFiltersContextProvider } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { Button } from '../../../components-v2/Button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../components-v2/Drawer/Drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { Search } from '../../../containers/templates/Search';
import { brandSecondary } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { AdvancedFiltersLegacyUI } from '../../AdvancedFilters/AdvancedFiltersLegacyUI';
import { ISelectOption } from '../../DeprecatedSelect';
import { OwnershipFilterDropdown } from '../../OwnershipFilterDropdown';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { Toggle } from '../../Toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { EmailDuplicateContactSendCondition } from '../EmailDuplicateContactSendCondition';
import { EmailRecipientsList } from '../EmailRecipientsList';
import { RenewalEmailPolicySelector } from '../PolicySelector';
import { TagEmailTagList } from '../TagEmailTagList';
import { styleSheet } from './styles';

export enum BulkDeliveryMethodOptions {
	All = 'all',
	WithEmails = 'withEmails',
	WithTexts = 'withTexts',
	WithAddress = 'withAddress',
}

export const BulkDeliveryMethodSelectOptions: { label: string; value: BulkDeliveryMethodOptions }[] = [
	{ label: 'Show All', value: BulkDeliveryMethodOptions.All },
	{ label: 'Have Emails', value: BulkDeliveryMethodOptions.WithEmails },
	{ label: 'Have Texts', value: BulkDeliveryMethodOptions.WithTexts },
	{ label: 'Have an Address', value: BulkDeliveryMethodOptions.WithAddress },
];

export const bulkDeliveryMethodFromFilterCriteria = (filterCriterias: IContactFilterCriteria[]) => {
	const isAll =
		['ReceiveEmail', 'ReceiveCard', 'ReceiveText'].every(capableOfValue => {
			for (const filterCriteria of filterCriterias) {
				if (
					VmUtils.findFilterCritiera({
						criteria: filterCriteria,
						predicate: c => c.property === ContactFilterCriteriaProperty.CapableOf && c.value === capableOfValue,
					})
				) {
					return true;
				}
			}
			return false;
		}) ||
		// handle the case where we don't have anything that specifies ContactFilterCriteriaProperty.CapableOf
		filterCriterias.every(filterCriteria =>
			Boolean(
				VmUtils.findFilterCritiera({
					criteria: filterCriteria,
					predicate: c => c.property !== ContactFilterCriteriaProperty.CapableOf,
				})
			)
		);
	if (isAll) {
		return BulkDeliveryMethodOptions.All;
	}
	if (
		filterCriterias.find(
			filter => filter.property === ContactFilterCriteriaProperty.CapableOf && filter.value === 'ReceiveEmail'
		) != null
	) {
		return BulkDeliveryMethodOptions.WithEmails;
	}
	if (
		filterCriterias.find(
			filter => filter.property === ContactFilterCriteriaProperty.CapableOf && filter.value === 'ReceiveText'
		) != null
	) {
		return BulkDeliveryMethodOptions.WithTexts;
	}
	if (
		filterCriterias.find(
			filter => filter.property === ContactFilterCriteriaProperty.CapableOf && filter.value === 'ReceiveCard'
		) != null
	) {
		return BulkDeliveryMethodOptions.WithAddress;
	}
	return null;
};
interface IProps extends IImpersonationContextComponentProps {
	canClearFilters?: boolean;
	ctaButtonText: string;
	disableContactAdding?: boolean;
	disableContactEdits?: boolean;
	disableContactRemoval?: boolean;
	disableCta?: boolean;
	disableOwnershipFilter?: boolean;
	emailComposer: ComposeEmailViewModel | ComposeResourceEmailViewModel | ComposeFollowUpEmailViewModel;
	onContactSelected?(contact: ContactViewModel): void;
	onCtaClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
	onScrollerRef?(ref?: HTMLElement): void;
	resetOnMount?: boolean;
	showForInitialSelection?: boolean;
	styles?: StyleDeclarationValue[];
	initialOwnershipFilter?: Api.IOwnershipFilter;
	sendFromOption?: Api.ISendOnBehalfPermission;
	selectedEmployee?: Api.IUser;
}

const getSelectedTags = (criteria: Api.IContactFilterCriteria[] = []) => {
	let selected: IContactFilterCriteria[] = [];

	criteria.forEach(c => {
		if (c.criteria) {
			selected = [...selected, ...getSelectedTags(c.criteria)];
		} else if (c.property === Api.ContactFilterCriteriaProperty.Tag) {
			selected.push(c);
		}
	});

	return selected;
};

function _TagEmailRecipientsList({
	canClearFilters = true,
	ctaButtonText,
	disableContactAdding,
	disableContactEdits,
	disableContactRemoval,
	disableCta,
	disableOwnershipFilter,
	emailComposer,
	onContactSelected,
	onCtaClick,
	onScrollerRef,
	resetOnMount = true,
	styles,
	initialOwnershipFilter,
	impersonationContext,
	sendFromOption,
	selectedEmployee,
}: IProps) {
	const calculateCustomOwnedByFilter = () => {
		// if sending on behalf of an employee, we want to allow easy filtering by contacts they own.
		// otherwise we should revert back to the normal 'Contacts owned by' option that makes you pick a user.
		const user = sendFromOption?.sender?.selectedUser ?? selectedEmployee?.id;
		return user && sendFromOption?.sender?.mode === Api.SendEmailFrom.SelectedUser && user !== userSession.user.id
			? ({
					criteria: { property: ContactFilterCriteriaProperty.OwnedBy, value: user },
					title: `Contacts they own`,
				} as Api.IOwnershipFilter)
			: undefined;
	};
	const userSession = useUserSession();
	const { logApiError, logEvent } = useEventLogging('TagEmailRecipientList');
	const resourceVm = emailComposer as ComposeResourceEmailViewModel;
	const isForResource = resourceVm?.resourceSelector;
	const alreadyExistsAsCampaign = !!emailComposer?.campaign?.id;
	const sortedCriteria = VmUtils.sortContactFilterCriteria(
		emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
	);
	const hasUnsubscribeTagNot =
		sortedCriteria.searches.filter(x => x.value === 'Unsubscribe' && x.op === Api.FilterOperator.Not).length > 0;
	const hasEmailBouncedTagNot =
		sortedCriteria.searches.filter(x => x.value === 'Email Bounced' && x.op === Api.FilterOperator.Not).length > 0;
	const [showSearch, setShowSearch] = React.useState(false);
	const [showingAdvancedFiltersFlyout, setShowingAdvancedFiltersFlyout] = React.useState(false);
	const [selectedResourceSortOption, setSelectedResourceSortOption] = React.useState<ISelectOption<string>>();
	const selectedDeliveryMethodOption = bulkDeliveryMethodFromFilterCriteria(sortedCriteria.filters);
	const [disableShowingDropdown, setDisableShowingDropdown] = React.useState(
		shouldDisableOwnershipDropdown(disableOwnershipFilter, sendFromOption)
	);
	const [customOwnedByFilter, setCustomOwnedByFilter] = React.useState(calculateCustomOwnedByFilter());

	// Fetch per-contact renewal policies for renewal flows
	const isRenewalFlow =
		resourceVm?.resourceSelector === Api.ResourceSelectorId.UpcomingPolicyRenewals ||
		resourceVm?.resourceSelector === Api.ResourceSelectorId.PolicyRenew;
	const { data: renewalPoliciesMap } = useContactRenewalPolicies({
		request: isRenewalFlow
			? {
					qualifier: resourceVm?.qualifier,
					policyActionableFilter: resourceVm?.policyActionableFilter,
					ownershipFilter: emailComposer?.emailMessage?.contactsFilterRequest?.ownershipFilter,
				}
			: undefined,
		resourceSelector: resourceVm?.resourceSelector,
		enabled: isRenewalFlow && (emailComposer?.recipients?.length ?? 0) > 0,
	});

	const selectedTags = getSelectedTags(
		emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
	);
	const onlyDefaultTagsAreSelected =
		selectedTags.length === 0 ||
		(selectedTags.length === DefaultBulkSendExcludedTags.size &&
			Array.from(DefaultBulkSendExcludedTags).filter(d => !!selectedTags.find(s => s.value === d)).length ===
				DefaultBulkSendExcludedTags.size);

	const useDefaultBulkContactRequest =
		alreadyExistsAsCampaign &&
		onlyDefaultTagsAreSelected &&
		emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest;

	const getInitialOwnershipFilter = (overridenFilter: Api.IOwnershipFilter) => {
		if (sendFromOption?.requiredFilter) {
			return sendFromOption.requiredFilter;
		}

		if (overridenFilter) {
			return overridenFilter;
		}
		const firstFilter = userSession.user.ownershipFilters?.length > 0 ? userSession.user.ownershipFilters[0] : null;
		const existingOwnershipFilter =
			emailComposer.emailMessage?.contactsFilterRequest?.ownershipFilter ??
			emailComposer.emailMessage?.contactsFilterRequest?.calculatedOwnershipFilter;
		return existingOwnershipFilter ?? firstFilter;
	};

	const [selectedOwnershipFilter, setSelectedOwnershipFilter] = React.useState(
		getInitialOwnershipFilter(initialOwnershipFilter)
	);
	React.useEffect(() => {
		setSelectedOwnershipFilter(getInitialOwnershipFilter(initialOwnershipFilter));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialOwnershipFilter]);

	const account = impersonationContext?.isValid ? impersonationContext.account : userSession.account;
	const duplicateContactsFeatureFlag = !!account.preferences.showDuplicateContacts;
	const canShowShowingDropdown = React.useMemo(() => {
		const canShowSuggestion = emailComposer.isSuggestion && userSession?.account?.isAdmin;
		return (
			(canShowSuggestion || (!emailComposer?.reachOutInfo && !alreadyExistsAsCampaign)) && !emailComposer.isFollowUp
		);
	}, [
		emailComposer.isSuggestion,
		emailComposer?.reachOutInfo,
		emailComposer.isFollowUp,
		userSession?.account?.isAdmin,
		alreadyExistsAsCampaign,
	]);
	const { labelByName: customFieldLabelByName } = useCustomFieldLabelMap(impersonationContext?.toJs());

	// Build a text summary for advanced filters (location, dates, custom fields) that aren't shown by tag list or ownership dropdown
	const filterSummaryText = React.useMemo(() => {
		const criteria = emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria;
		if (!criteria?.length) {
			return '';
		}

		const parts: string[] = [];

		// Location description — find City/StateProvince/PostalCode as direct leaf or inside Or groups
		const findCriteriaForProperty = (property: Api.ContactFilterCriteriaProperty) => {
			const direct = criteria.find(c => c.property === property);
			if (direct) return direct;
			for (const c of criteria) {
				if (c.op === Api.FilterOperator.Or && c.criteria?.length && c.criteria[0]?.property === property) {
					return c.criteria[0];
				}
			}
			return undefined;
		};

		const cityCriteria = findCriteriaForProperty(Api.ContactFilterCriteriaProperty.City);
		const stateCriteria = findCriteriaForProperty(Api.ContactFilterCriteriaProperty.StateProvince);
		const zipCriteria = findCriteriaForProperty(Api.ContactFilterCriteriaProperty.PostalCode);
		const locationParts: string[] = [];
		if (cityCriteria?.value) locationParts.push(cityCriteria.value);
		if (stateCriteria?.value) locationParts.push(stateCriteria.value);
		if (zipCriteria?.value) locationParts.push(zipCriteria.value);
		if (locationParts.length > 0) {
			parts.push(`in ${locationParts.join(', ')}`);
		}

		const jobTitleCriteria = findCriteriaForProperty(Api.ContactFilterCriteriaProperty.JobTitle);
		if (jobTitleCriteria?.value) {
			parts.push(`with title "${jobTitleCriteria.value}"`);
		}

		// Date range description helper
		const getDateDescription = (property: Api.ContactFilterCriteriaProperty, propertyName: string) => {
			const dateCriteria = criteria.find(criterion => criterion.criteria?.find(sub => sub.property === property));
			if (!dateCriteria) return '';

			if (dateCriteria.criteria?.find(c => c.value === 'never')) {
				return 'never contacted before';
			}

			const relativeDates = dateCriteria.criteria?.find(c => c.values && Object.keys(c.values).length > 0);
			if (relativeDates) {
				const years = relativeDates.values['years'];
				const months = relativeDates.values['months'];
				const days = relativeDates.values['days'];
				if (years && !isNaN(parseInt(years))) {
					const v = -1 * parseInt(years);
					return `last contacted more than ${v} year${v > 1 ? 's' : ''} ago`;
				}
				if (months && !isNaN(parseInt(months))) {
					const v = -1 * parseInt(months);
					return `last contacted more than ${v} month${v > 1 ? 's' : ''} ago`;
				}
				if (days && !isNaN(parseInt(days))) {
					const v = -1 * parseInt(days);
					return `last contacted more than ${v} day${v > 1 ? 's' : ''} ago`;
				}
			}

			const startDate = moment(dateCriteria.criteria?.find(c => c.op === Api.FilterOperator.Gte)?.value);
			const endDateCriteria = dateCriteria.criteria?.find(c => c.op === Api.FilterOperator.Lte);
			const endDate = endDateCriteria?.value ? moment(endDateCriteria.value) : undefined;

			if (!startDate?.isValid() && !endDate) return '';
			if (!endDate) return `with a ${propertyName} after ${startDate.format('MMMM Do, YYYY')}`;
			return `with a ${propertyName} between ${startDate.format('MMMM Do, YYYY')} and ${endDate.format('MMMM Do, YYYY')}`;
		};

		const lastCommDesc = getDateDescription(
			Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			'last communication date'
		);
		if (lastCommDesc) parts.push(lastCommDesc);

		const creationDesc = getDateDescription(Api.ContactFilterCriteriaProperty.CreationDate, 'creation date');
		if (creationDesc) parts.push(creationDesc);

		// Custom field criteria
		const customFieldCriteria = criteria.filter(
			c => c.property === Api.ContactFilterCriteriaProperty.CustomField && c.value
		);
		customFieldCriteria.forEach(c => {
			const fieldNameValue = c.values?.FieldName;
			const label = (fieldNameValue && customFieldLabelByName.get(fieldNameValue)) || fieldNameValue || 'custom field';
			parts.push(`with ${label} "${c.value}"`);
		});

		return parts.join(' ');
	}, [emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria, customFieldLabelByName]);

	const resourceSortOptions = React.useMemo(() => {
		let type = '';
		if (isForResource) {
			switch (resourceVm.resourceSelector) {
				case Api.ResourceSelectorId.PolicyRenew:
					type = 'renewal date';
					break;
				case Api.ResourceSelectorId.FinancialReview:
					type = 'review date';
					break;
				case Api.ResourceSelectorId.HappyBirthday:
					type = 'birthday';
					break;
				default:
					break;
			}
		}
		return [
			{
				dataContext: ContactSortKey.Handle,
				id: 'sort-option-first-name',
				text: 'Sort by first name A - Z',
				type: 'default',
			},
			{
				dataContext: ContactSortKey.HandleReverse,
				id: 'sort-option-last-name',
				text: 'Sort by last name A - Z',
				type: 'default',
			},
			{
				dataContext: ContactSortKey.KeyDate,
				id: 'sort-option-type',
				text: `Sort by ${type}`,
				type: 'default',
			},
		] as ISelectOption<ContactSortKey>[];
	}, [isForResource, resourceVm]);

	const getContacts = React.useCallback(
		(reset = false, loadApproximation = false) =>
			() => {
				const resourceSelector = (emailComposer as ComposeResourceEmailViewModel)?.resourceSelector ?? null;

				let makeRequest = true;

				if (!resourceSelector) {
					if (emailComposer.emailMessage.contactsToAdd.length > 0 && onlyDefaultTagsAreSelected) {
						// if there are individual contacts selected and no tags have been selected make the getContacts request.
						makeRequest = true;
					} else if (!emailComposer?.emailMessage?.contactsFilterRequest && !emailComposer?.isFollowUp) {
						makeRequest = false;
					}
				}
				if (loadApproximation && !emailComposer?.isFollowUp) {
					const approxPromise = isForResource
						? resourceVm.getEmailApproximationWithResource()
						: // If existing campaign and  only default tags, pass default filter else use normal approx.
							useDefaultBulkContactRequest
							? emailComposer.emailMessage.getEmailApproximation(DefaultBulkContactsRequest)
							: emailComposer.emailMessage.getEmailApproximation();
					approxPromise?.catch((error: IOperationResultNoValue) => {
						logApiError('HasEmailApproximationLoad-Error', error);
					});
				}
				// if there are individual contacts selected do not make the getContacts request if there are no tags selected.
				if (makeRequest) {
					if (reset) {
						emailComposer?.resetRecipientsResultsList();
					}
					const promise = emailComposer?.getNextBatchOfRecipients();
					if (promise) {
						logEvent('ContactsLoad');
						promise.catch((error: IOperationResultNoValue) => {
							logApiError('ContactsLoad-Error', error);
						});
					}
					return promise;
				}
			},
		[
			emailComposer,
			isForResource,
			logApiError,
			logEvent,
			onlyDefaultTagsAreSelected,
			resourceVm,
			useDefaultBulkContactRequest,
		]
	);
	const handleDuplicateContactsToggleChange = () => {
		emailComposer.emailMessage.contactsFilterRequest.groupByDuplicate =
			!emailComposer.emailMessage.contactsFilterRequest.groupByDuplicate;

		/** Below's taken from the getContacts logic because calling getContacts(true, true) here often ends up doing nothing
		 * because you can't control makeRequest from the outside.
		 **/
		const approxPromise = isForResource
			? resourceVm.getEmailApproximationWithResource()
			: // If existing campaign and  only default tags, pass default filter else use normal approx.
				useDefaultBulkContactRequest
				? emailComposer.emailMessage.getEmailApproximation(DefaultBulkContactsRequest)
				: emailComposer.emailMessage.getEmailApproximation();
		approxPromise?.catch((error: IOperationResultNoValue) => {
			logApiError('HasEmailApproximationLoad-Error', error);
		});
		emailComposer?.resetRecipientsResultsList();
		const promise = emailComposer?.getNextBatchOfRecipients();
		if (promise) {
			logEvent('ContactsLoad');
			promise.catch((error: IOperationResultNoValue) => {
				logApiError('ContactsLoad-Error', error);
			});
		}
		return promise;
	};
	const handleGroupByHouseholdToggleChange = () => {
		emailComposer.emailMessage.groupByHousehold = !emailComposer.emailMessage.groupByHousehold;

		/** Below's taken from the getContacts logic because calling getContacts(true, true) here often ends up doing nothing
		 * because you can't control makeRequest from the outside.
		 **/
		const approxPromise = isForResource
			? resourceVm.getEmailApproximationWithResource()
			: // If existing campaign and  only default tags, pass default filter else use normal approx.
				useDefaultBulkContactRequest
				? emailComposer.emailMessage.getEmailApproximation(DefaultBulkContactsRequest)
				: emailComposer.emailMessage.getEmailApproximation();
		approxPromise?.catch((error: IOperationResultNoValue) => {
			logApiError('HasEmailApproximationLoad-Error', error);
		});
		emailComposer?.resetRecipientsResultsList();
		const promise = emailComposer?.getNextBatchOfRecipients();
		if (promise) {
			logEvent('ContactsLoad');
			promise.catch((error: IOperationResultNoValue) => {
				logApiError('ContactsLoad-Error', error);
			});
		}
		return promise;
	};

	const onFilterOptionChanged = (selectedOption: Api.IOwnershipFilter) => {
		// do not want to change filter if the campaign has already been created.
		if (emailComposer?.campaign?.id) {
			return;
		}
		emailComposer.userSelectedFilterOption = selectedOption; // preserve the user's selection in case of customizing per contact
		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = selectedOption;
		}
		setSelectedOwnershipFilter(selectedOption);
		getContacts(true, true)();
	};
	const onDeliveryMethodOptionChanged = (selectedOption: BulkDeliveryMethodOptions) => {
		const sorted = VmUtils.sortContactFilterCriteria(
			emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
		);
		const filterCriteriaToBeAdded: Record<string, IContactFilterCriteria> = {
			all: {
				criteria: [
					{
						property: ContactFilterCriteriaProperty.CapableOf,
						value: 'ReceiveEmail',
					},
					{
						property: ContactFilterCriteriaProperty.CapableOf,
						value: 'ReceiveCard',
					},
					{
						property: ContactFilterCriteriaProperty.CapableOf,
						value: 'ReceiveText',
					},
				],
				op: Api.FilterOperator.Or,
			},
			withEmails: {
				property: ContactFilterCriteriaProperty.CapableOf,
				value: 'ReceiveEmail',
			},
			withTexts: {
				property: ContactFilterCriteriaProperty.CapableOf,
				value: 'ReceiveText',
			},
			withAddress: {
				property: ContactFilterCriteriaProperty.CapableOf,
				value: 'ReceiveCard',
			},
		};
		const filterRequest: IContactsFilterRequest = {
			criteria: VmUtils.concatContactFilterCriteria(
				[
					...(sorted.filters || []).filter(
						x => !VmUtils.areAllCriteriaOfProperty({ criteria: x, property: ContactFilterCriteriaProperty.CapableOf })
					),
					...sorted.searches,
				],
				selectedOption ? [filterCriteriaToBeAdded[selectedOption]] : []
			),
		};
		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest.contactFilterRequest = filterRequest;
		}
		getContacts(true, true)();
	};

	const onAdvancedFiltersFlyoutSave = (
		filterCriteria: IContactFilterCriteria[],
		householdGrouping?: boolean,
		ownershipFilter?: Api.IOwnershipFilter
	) => {
		const nextFilterRequest: IContactsFilterRequest = {
			criteria: filterCriteria,
		};
		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest.contactFilterRequest = nextFilterRequest;
			emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = ownershipFilter;
		}
		if (householdGrouping !== null) {
			emailComposer.emailMessage.groupByHousehold = householdGrouping;
			emailComposer.emailMessage.contactsFilterRequest.groupByDuplicate = false;
			emailComposer.emailMessage.contactsFilterRequest.groupByHousehold = householdGrouping;
		}
		getContacts(true, true)();
		setShowingAdvancedFiltersFlyout(false);
		setSelectedOwnershipFilter(ownershipFilter);
		if (
			ownershipFilter?.criteria?.property === ContactFilterCriteriaProperty.OwnedBy &&
			ownershipFilter?.criteria?.value !== userSession.user.id
		) {
			setCustomOwnedByFilter({ ...ownershipFilter, title: 'Contacts they own' });
		}
	};
	const onAdvancedFiltersFlyoutCancel = () => {
		setShowingAdvancedFiltersFlyout(false);
	};
	const onSearchesChanged = (searches: IContactFilterCriteria[]) => {
		const sorted = VmUtils.sortContactFilterCriteria(
			(emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest || {}).criteria
		);
		const nextFilterRequest: IContactsFilterRequest = {
			...(emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest || {}),
			criteria: sorted.filters.concat(searches),
		};

		emailComposer?.resetRecipientsResultsList();
		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest.contactFilterRequest = nextFilterRequest;
		}
		getContacts(true, true)();
	};
	const onResourceSortOptionClick = (_selectedResourceSortOption: ISelectOption<ContactSortKey>) => {
		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest = {
				...emailComposer.emailMessage.contactsFilterRequest,
				sortAscending: true,
				sortProperty: _selectedResourceSortOption.dataContext as any,
			};
		}
		getContacts(true, true)();
		setSelectedResourceSortOption(_selectedResourceSortOption);
	};

	React.useEffect(() => {
		setDisableShowingDropdown(shouldDisableOwnershipDropdown(disableOwnershipFilter, sendFromOption));

		const initalFilterIsOwnedBySomeoneElse =
			initialOwnershipFilter?.criteria.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
			initialOwnershipFilter?.criteria?.value !== userSession.user.id;
		const isSwitchingFromContactsTheyOwn =
			emailComposer?.emailMessage?.options?.sendEmailFrom !== Api.SendEmailFrom.SelectedUser &&
			selectedOwnershipFilter?.criteria.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
			selectedOwnershipFilter?.criteria?.value !== userSession.user.id &&
			!initalFilterIsOwnedBySomeoneElse;
		const isSwitchingSelectedUser =
			emailComposer?.emailMessage?.options?.sendEmailFrom === Api.SendEmailFrom.SelectedUser;

		setCustomOwnedByFilter(calculateCustomOwnedByFilter());

		if (sendFromOption?.sender) {
			if (sendFromOption.sender.mode !== Api.SendEmailFrom.CurrentUser) {
				onFilterOptionChanged(
					sendFromOption?.requiredFilter ??
						userSession.user.ownershipFilters?.find(
							option => option.criteria?.property === ContactFilterCriteriaProperty.All
						)
				);
			}
		} else if (isSwitchingFromContactsTheyOwn || isSwitchingSelectedUser) {
			onFilterOptionChanged(
				userSession.user.ownershipFilters?.find(
					option => option.criteria?.property === ContactFilterCriteriaProperty.All
				)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		disableOwnershipFilter,
		emailComposer?.emailMessage?.options?.sendEmailFromUserId,
		sendFromOption,
		userSession,
		selectedEmployee,
	]);

	// effect for what used to be setDefaultSelectedOption() on mount
	React.useEffect(() => {
		if (!emailComposer?.allowSenderSelection) {
			return;
		}
		const ownedByFilter = emailComposer.emailMessage.contactsFilterRequest?.ownershipFilter;

		let defaultSelectedOption: Api.IOwnershipFilter;
		// If OwnedBy has been explicitly set, do not augment the filter at all.
		// There are a few future scenarios where this early return could be a problem:
		// - We ever have a scenario that we'd allow providing or setting emailComposer.userSelectedFilterOption
		// - If we show the Showing filter/dropdown, we wouldn't have a filter option for the specified user.
		if (ownedByFilter !== null) {
			return;
		}
		// If the user has already changed the filter, use that value over the defaults
		if (emailComposer?.userSelectedFilterOption) {
			defaultSelectedOption = emailComposer?.userSelectedFilterOption;
		} else if (emailComposer?.emailMessage?.isSendFromOption(Api.SendEmailFrom.SelectedUser)) {
			defaultSelectedOption = userSession.user.ownershipFilters?.find(
				x =>
					x.criteria?.property === ContactFilterCriteriaProperty.OwnedBy &&
					x.criteria?.value === emailComposer?.emailMessage?.options?.sendEmailFromUserId
			);
		} else {
			defaultSelectedOption = userSession.user.ownershipFilters?.find(
				x => x.criteria?.property === ContactFilterCriteriaProperty.All
			);
		}
		const sorted = VmUtils.sortContactFilterCriteria(
			emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
		);
		const filterRequest: IContactsFilterRequest = {
			criteria: VmUtils.concatContactFilterCriteria([
				...(sorted.filters || []).filter(x => x.property !== ContactFilterCriteriaProperty.OwnedBy),
				...sorted.searches,
			]),
		};

		if (isForResource) {
			filterRequest.criteria = VmUtils.concatContactFilterCriteria(filterRequest.criteria, [
				{
					criteria: [
						{
							property: ContactFilterCriteriaProperty.CapableOf,
							value: 'ReceiveEmail',
						},
						{
							property: ContactFilterCriteriaProperty.CapableOf,
							value: 'ReceiveCard',
						},
						{
							property: ContactFilterCriteriaProperty.CapableOf,
							value: 'ReceiveText',
						},
					],
					op: Api.FilterOperator.Or,
				},
			]);
		}

		if (emailComposer?.emailMessage?.contactsFilterRequest) {
			emailComposer.emailMessage.contactsFilterRequest.contactFilterRequest = filterRequest;
			emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = defaultSelectedOption;
		}
		getContacts(resetOnMount, true)();
	}, [
		emailComposer,
		getContacts,
		isForResource,
		resetOnMount,
		userSession,
		userSession.account.preferences?.showAllContactsByDefault,
		userSession.user.ownershipFilters,
	]);

	React.useEffect(() => {
		if (
			!showSearch &&
			!!emailComposer &&
			!isForResource &&
			!emailComposer.isFollowUp &&
			!emailComposer.emailMessage?.hasUserSelectedContactFilterSearchCriteria &&
			!emailComposer.campaign?.id &&
			emailComposer.emailMessage.contactsToAdd?.length === 0 &&
			!emailComposer.reachOutInfo
		) {
			logEvent('ShowGlobalSearchPanel');
			// reset excluded
			emailComposer.emailMessage.contactsToOmit.clear();
			setShowSearch(true);
		}
	}, [
		showSearch,
		emailComposer,
		isForResource,
		emailComposer.isFollowUp,
		emailComposer.emailMessage?.hasUserSelectedContactFilterSearchCriteria,
		emailComposer.campaign?.id,
		emailComposer.emailMessage.contactsToAdd,
		emailComposer.reachOutInfo,
		logEvent,
	]);
	function renderContactCount() {
		const approximation = isForResource
			? resourceVm?.approximation
			: emailComposer.emailMessage?.contactEmailApproximation;

		const sortOption = selectedResourceSortOption || resourceSortOptions[0];
		const followUpCount =
			emailComposer.emailMessage?.options?.followUp?.excludeContactIds?.length > 0 &&
			emailComposer.followUpCount > 0 &&
			emailComposer.campaign?.status !== Api.EmailSendStatus.Queued
				? emailComposer.followUpCount - emailComposer.emailMessage?.options?.followUp?.excludeContactIds?.length
				: (emailComposer.followUpCount ?? 0);
		return (
			<div className={css(styleSheet.headerCount, styleSheet.headerCountPolicies)}>
				<RecipientsCount
					className={css(
						styleSheet.headerCountNumbers,
						(approximation?.total || 0) <= 0 && !emailComposer.followUpCount && styleSheet.headerCountNumbersImportant
					)}
					approximation={emailComposer?.isFollowUp ? null : approximation}
					followUpCount={
						emailComposer.emailMessage?.options?.followUp &&
						emailComposer.emailMessage?.options?.followUp._type !== 'DonationFollowUpOptions'
							? followUpCount
							: undefined
					}
					hasUnsubscribeTagNot={hasUnsubscribeTagNot}
					hasEmailBouncedTagNot={hasEmailBouncedTagNot}
					loading={isForResource ? resourceVm.isApproximating : emailComposer.emailMessage.isApproximating}
					fallbackCount={
						onlyIndividualContactsSelected
							? emailComposer.emailMessage.contactsToAdd.length
							: emailComposer.estimatedRecipientsTotal
					}
					styles={[isForResource && styleSheet.recipientCountWithSort]}
				/>
				{isForResource && (
					<div>
						<Select
							value={sortOption.id}
							onValueChange={(value: string) => {
								const option = resourceSortOptions.find(o => o.id === value);
								if (option) {
									onResourceSortOptionClick(option);
								}
							}}
						>
							<SelectTrigger styles={[styleSheet.sort, styleSheet.sortTrigger]}>
								<span className={css(styleSheet.sortText)}>{sortOption.text}</span>
							</SelectTrigger>
							<SelectContent>
								{resourceSortOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			</div>
		);
	}
	const shouldShowGroupByHouseholdToggle = userSession.account.features?.households?.enabled;
	const renderRecipientsTagHeader = () => {
		const type = isForResource
			? resourceVm.resourceSelector === Api.ResourceSelectorId.PolicyRenew
				? 'Renewals'
				: resourceVm.resourceSelector === Api.ResourceSelectorId.FinancialReview
					? 'Reviews'
					: ''
			: '';

		const title =
			isForResource &&
			(resourceVm.resourceSelector === Api.ResourceSelectorId.PolicyRenew ||
				resourceVm.resourceSelector === Api.ResourceSelectorId.FinancialReview)
				? `${moment()
						.set('month', parseInt(resourceVm.qualifier, 10) - 1)
						.format('MMMM')} ${type}`
				: 'Recipients';

		const tagSearchCriteria = VmUtils.sortContactFilterCriteria(
			emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria
		).searches.filter(x => VmUtils.isTagSearchContactFilterCriteria(x));
		return (
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.headerTitle)}>
					<div>{title}</div>
					{!emailComposer.isFollowUp && emailComposer.allowContactFilterTagSearchChanges ? (
						<>
							<button className={css(baseStyleSheet.brandLink)} onClick={() => setShowingAdvancedFiltersFlyout(true)}>
								<div>Advanced Filters</div>
							</button>
							<Drawer
								open={showingAdvancedFiltersFlyout}
								onOpenChange={open => !open && onAdvancedFiltersFlyoutCancel()}
								swipeDirection='right'
							>
								<DrawerContent>
									<DrawerHeader>
										<DrawerTitle>Advanced Filters</DrawerTitle>
									</DrawerHeader>
									<AdvancedFiltersContextProvider
										initialFilterCriteria={
											emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria || []
										}
										initialOwnershipFilter={selectedOwnershipFilter}
										initialSelectedUserId={emailComposer.emailMessage.options.sendEmailFromUserId}
										onSave={onAdvancedFiltersFlyoutSave}
										refresh={showingAdvancedFiltersFlyout}
										initialHouseholdGroupingOn={emailComposer.emailMessage.groupByHousehold}
										impersonationContext={impersonationContext?.toJs()}
									>
										<AdvancedFiltersLegacyUI
											hideHeader
											onDismissFlyout={onAdvancedFiltersFlyoutCancel}
											showPopularTags
											showSavedSearches
											savedSearchesReadOnly
											showHousehold={userSession.account.features?.households?.enabled}
											showOwnershipFilters
											showStatusFilters
											showLocationFilters
											showJobTitleFilter
											showCompanyTagFilters
											showCustomFields
											showCreateDateFilters
											showLastCommunicationDate
											showKeyFactFilters={!isForResource}
											disableOwnershipFilters={disableShowingDropdown}
											impersonationContext={impersonationContext?.toJs()}
										/>
									</AdvancedFiltersContextProvider>
								</DrawerContent>
							</Drawer>
						</>
					) : null}
				</div>
				{(emailComposer as ComposeResourceEmailViewModel).resourceSelector === Api.ResourceSelectorId.PolicyRenew &&
				!isPolicyBoardEnabled(userSession.account?.features?.boards) ? (
					<RenewalEmailPolicySelector />
				) : null}
				{!emailComposer.isFollowUp && (emailComposer?.selectedReachOutTags.length || tagSearchCriteria?.length > 0) ? (
					<TagEmailTagList
						emailComposer={emailComposer as ComposeEmailViewModel}
						onSearchesChanged={onSearchesChanged}
						readOnly={false}
					/>
				) : null}
				{filterSummaryText ? <div className={css(styleSheet.filterSummary)}>{filterSummaryText}</div> : null}
				{!isForResource && hasContactFilter && duplicateContactsFeatureFlag ? (
					<div>
						<EmailDuplicateContactSendCondition
							filterRequest={emailComposer.emailMessage.bulkContactsRequest}
							isSelected={emailComposer.emailMessage.contactsFilterRequest.groupByDuplicate ?? false}
							onChange={handleDuplicateContactsToggleChange}
						/>
					</div>
				) : null}
				{!isForResource && shouldShowGroupByHouseholdToggle ? (
					<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
						<Toggle
							id='groupByHouseholdToggle'
							isOn={emailComposer.emailMessage.groupByHousehold ?? false}
							text='Group recipents by household'
							textStyles={[bs.textXs, bs.textTitles]}
							uncheckedColor='#ccc'
							onToggleCheckChanged={handleGroupByHouseholdToggleChange}
						/>
					</div>
				) : null}
				<div className={css(baseStyleSheet.horizontalStack, baseStyleSheet.flex)}>
					{canShowShowingDropdown ? (
						<div className={css(baseStyleSheet.verticalStack, styleSheet.headerOptions)}>
							<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
								<span>Showing:</span>
								{disableShowingDropdown && !disableOwnershipFilter ? (
									<Tooltip>
										<TooltipTrigger>
											<InfoIcon fillColor={brandSecondary} className={css(bs.block)} />
										</TooltipTrigger>
										<TooltipContent styles={[tooltipStyles.contentWithMaxWidth]}>
											Disabled because you're sending on behalf of the contact owner. Change "Send From" selection to
											make a selection here.
										</TooltipContent>
									</Tooltip>
								) : null}
							</div>
							<OwnershipFilterDropdown
								initialOwnershipFilter={selectedOwnershipFilter}
								onOwnershipFilterChanged={onFilterOptionChanged}
								disabled={
									disableShowingDropdown ||
									(emailComposer.emailMessage.contactsToAdd.length > 0 && onlyDefaultTagsAreSelected)
								}
								allowSelectAUserOption={false}
								impersonationContext={impersonationContext?.toJs()}
								ownedByFilter={customOwnedByFilter}
							/>
						</div>
					) : null}
					{isForResource ? (
						<div className={css(baseStyleSheet.verticalStack, styleSheet.headerOptions)}>
							<div>Filter by available info</div>
							<Select
								items={BulkDeliveryMethodSelectOptions}
								onValueChange={onDeliveryMethodOptionChanged}
								value={selectedDeliveryMethodOption}
							>
								<SelectTrigger styles={[styleSheet.filterDropdownTrigger]}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{BulkDeliveryMethodSelectOptions.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : null}
				</div>
				{renderContactCount()}
			</div>
		);
	};
	const renderRecipientsSearch = () => {
		return (
			<Search
				onNextClick={explicitelyPreventContactFilterRequest => {
					if (explicitelyPreventContactFilterRequest) {
						emailComposer?.setAllowContactFilterTagSearchChangesOverride(false);
					}
					onApplySearchCriteria();
				}}
				canClearFilters={canClearFilters}
			/>
		);
	};

	const onApplySearchCriteria = () => {
		if (emailComposer.emailMessage?.hasUserSelectedContactFilterSearchCriteria) {
			emailComposer?.reloadRecipientsList();
		} else {
			emailComposer?.resetRecipientsResultsList();
		}
		// TODO: This hides the search, then it resets above which clears criteria and starts over
		setShowSearch(false);
	};

	if (showSearch) {
		return renderRecipientsSearch();
	}

	const hasContactFilter = !!emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest;

	const onlyIndividualContactsSelected =
		emailComposer.emailMessage.contactsToAdd.length > 0 && onlyDefaultTagsAreSelected;

	return (
		<>
			<div className={`${css(styleSheet.TagEmailRecipientsList, ...(styles || []))}`}>
				{renderRecipientsTagHeader()}
				<EmailRecipientsList
					allowNoContactsMessaging={
						selectedOwnershipFilter?.criteria?.property === Api.ContactFilterCriteriaProperty.Connections
					}
					bulkEmailComposer={emailComposer}
					styles={[styleSheet.list]}
					disableContactAdding={emailComposer.isFollowUp ? true : disableContactAdding}
					disableContactEdits={emailComposer.isFollowUp ? true : disableContactEdits}
					disableContactRemoval={disableContactRemoval}
					onContactSelected={onContactSelected}
					onScrollerRef={onScrollerRef}
					onScrollToBottom={getContacts(
						false,
						emailComposer?.emailMessage &&
							!emailComposer.emailMessage.isApproximating &&
							!emailComposer.emailMessage.contactEmailApproximation
					)}
					renewalPoliciesMap={renewalPoliciesMap}
				/>
			</div>
			<footer className={css(styleSheet.footer)}>
				<Button
					styles={[bs.wFull]}
					onClick={onCtaClick}
					disabled={
						disableCta ||
						emailComposer?.recipients?.length <= 0 ||
						emailComposer?.emailMessage.isBusy ||
						emailComposer?.isBusy
					}
					isLoading={emailComposer?.emailMessage.isBusy || emailComposer?.isBusy}
				>
					{ctaButtonText}
				</Button>
			</footer>
		</>
	);
}

const TagEmailRecipientsListAsObserver = observer(_TagEmailRecipientsList);
export const TagEmailRecipientsList = inject(ImpersonationContextKey)(TagEmailRecipientsListAsObserver);
